import { useId, useState } from 'react';
import { Link } from 'react-router';
import { Inbox } from 'lucide-react';
import type { ComponentProps, ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { t } from '../lib/i18n';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-hover border border-transparent shadow-sm',
  secondary: 'bg-white text-slate-800 border border-slate-300 hover:bg-slate-50',
  danger: 'bg-red-700 text-white border border-transparent hover:bg-red-800 shadow-sm',
  ghost: 'bg-transparent text-slate-700 border border-transparent hover:bg-slate-100',
};

export function Button({
  variant = 'secondary',
  className = '',
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      type={type}
      className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors sm:min-h-9 disabled:cursor-not-allowed disabled:opacity-50 ${VARIANT[variant]} ${className}`}
      {...rest}
    />
  );
}

export function LinkButton({ variant = 'secondary', className = '', ...rest }: ComponentProps<typeof Link> & { variant?: Variant }) {
  return (
    <Link
      className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors sm:min-h-9 ${VARIANT[variant]} ${className}`}
      {...rest}
    />
  );
}

/** Skeleton placeholder shown while a list or page loads (announces "Loading…" to screen readers). */
export function Spinner() {
  return (
    <div role="status" aria-busy="true" className="space-y-3 p-2">
      <span className="sr-only">{t('common.loading')}</span>
      <div className="cs-skeleton h-6 w-1/3" />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="cs-skeleton h-12 w-full" />
      ))}
    </div>
  );
}

export function Card({
  title,
  description,
  footer,
  children,
  className = '',
}: {
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="p-5 sm:p-6">
        {title ? <h2 className="text-base font-semibold text-slate-900">{title}</h2> : null}
        {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
        <div className={title || description ? 'mt-4' : ''}>{children}</div>
      </div>
      {footer ? <div className="flex justify-end gap-2 rounded-b-xl border-t border-slate-200 bg-slate-50 px-5 py-3 sm:px-6">{footer}</div> : null}
    </section>
  );
}

export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

/** A white bar that holds a list's search box and filters. */
export function FilterBar({ children, columns = 'sm:grid-cols-3' }: { children: ReactNode; columns?: string }) {
  return (
    <div role="group" aria-label={t('common.filters')} className={`mb-4 grid gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm ${columns}`}>
      {children}
    </div>
  );
}

/** A small line icon, one sentence and (optionally) a button, for lists with nothing in them. */
export function EmptyState({ message, action }: { message?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
        <Inbox aria-hidden="true" className="h-6 w-6" />
      </span>
      <p className="text-sm text-slate-600">{message ?? t('common.noResults')}</p>
      {action}
    </div>
  );
}

/** Round avatar with initials. */
export function Avatar({ name, className = 'h-8 w-8 text-xs' }: { name: string; className?: string }) {
  return (
    <span aria-hidden="true" className={`inline-flex shrink-0 items-center justify-center rounded-full bg-brand-soft font-semibold text-slate-800 ring-1 ring-slate-200 ${className}`}>
      {initials(name)}
    </span>
  );
}

export function initials(name: string): string {
  const parts = name.trim().split(/s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'success' | 'info'; children: ReactNode }) {
  const styles = {
    error: 'border-red-200 bg-red-50 text-red-900',
    success: 'border-green-200 bg-green-50 text-green-900',
    info: 'border-sky-200 bg-sky-50 text-sky-900',
  }[kind];
  return (
    <div role={kind === 'error' ? 'alert' : 'status'} className={`rounded-md border p-3 text-sm ${styles}`}>
      {children}
    </div>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  const styles = {
    neutral: 'bg-slate-100 text-slate-700',
    good: 'bg-green-100 text-green-900',
    warn: 'bg-amber-100 text-amber-900',
    bad: 'bg-red-100 text-red-900',
  }[tone];
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${styles}`}>{children}</span>;
}

const INPUT =
  'block w-full min-h-10 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-500 hover:border-slate-400 disabled:bg-slate-100 disabled:text-slate-600 aria-[invalid=true]:border-red-600 sm:min-h-9';

interface FieldShell {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
}

function describe(id: string, hint: ReactNode, error: string | undefined): string | undefined {
  return [hint ? `${id}-hint` : '', error ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined;
}

function FieldNotes({ id, hint, error }: { id: string; hint: ReactNode; error: string | undefined }) {
  return (
    <>
      {hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-err`} className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </>
  );
}

export function TextField({ label, hint, error, className = '', ...rest }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-800">
        {label}
      </label>
      <input id={id} className={INPUT} aria-invalid={error ? true : undefined} aria-describedby={describe(id, hint, error)} {...rest} />
      <FieldNotes id={id} hint={hint} error={error} />
    </div>
  );
}

export function SelectField({
  label,
  hint,
  error,
  children,
  className = '',
  ...rest
}: FieldShell & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-800">
        {label}
      </label>
      <select id={id} className={INPUT} aria-invalid={error ? true : undefined} aria-describedby={describe(id, hint, error)} {...rest}>
        {children}
      </select>
      <FieldNotes id={id} hint={hint} error={error} />
    </div>
  );
}

export function CheckboxField({
  label,
  hint,
  className = '',
  ...rest
}: { label: ReactNode; hint?: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const id = useId();
  return (
    <div className={className}>
      <div className="flex items-start gap-3">
        <input id={id} type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-[var(--brand-primary)]" aria-describedby={hint ? `${id}-hint` : undefined} {...rest} />
        <label htmlFor={id} className="text-sm">
          {label}
        </label>
      </div>
      {hint ? (
        <p id={`${id}-hint`} className="ml-8 mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** A destructive button that asks "are you sure?" in place before doing anything. */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  disabled,
  pending,
}: {
  label: ReactNode;
  confirmLabel?: ReactNode;
  onConfirm: () => void;
  disabled?: boolean;
  pending?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <Button variant="danger" disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </Button>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2" role="group" aria-label={t('common.confirm')}>
      <span className="text-sm">{t('common.areYouSure')}</span>
      <Button
        variant="danger"
        disabled={pending}
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
      >
        {confirmLabel ?? label}
      </Button>
      <Button onClick={() => setAsking(false)}>{t('common.cancel')}</Button>
    </span>
  );
}

export function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <nav className="mt-4 flex items-center justify-between gap-3" aria-label={t('common.pagination')}>
      <Button disabled={page <= 1} onClick={() => onPage(page - 1)}>
        {t('common.previous')}
      </Button>
      <span className="text-sm" aria-live="polite">
        {t('common.page', { page, pages })}
      </span>
      <Button disabled={page >= pages} onClick={() => onPage(page + 1)}>
        {t('common.next')}
      </Button>
    </nav>
  );
}

export function SortHeader({
  label,
  field,
  sort,
  order,
  onSort,
}: {
  label: ReactNode;
  field: string;
  sort: string | undefined;
  order: 'asc' | 'desc';
  onSort: (field: string) => void;
}) {
  const active = sort === field;
  return (
    <th scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600" aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="inline-flex items-center gap-1 font-semibold hover:text-slate-900" onClick={() => onSort(field)}>
        {label}
        <span aria-hidden="true">{active ? (order === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}

export function Th({ children }: { children?: ReactNode }) {
  return (
    <th scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600">
      {children}
    </th>
  );
}

export function TableWrap({ children, caption }: { children: ReactNode; caption: string }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[40rem] border-collapse text-sm">
        <caption className="sr-only">{caption}</caption>
        {children}
      </table>
    </div>
  );
}

export function EmptyRow({ cols, action }: { cols: number; action?: ReactNode }) {
  return (
    <tr>
      <td colSpan={cols}>
        <EmptyState action={action} />
      </td>
    </tr>
  );
}
